import { useId, useState } from 'react';
import type { AllergenOptionResponse } from '../api/types';
import { useI18n } from '../i18n/I18nProvider';
import { Input, type InputProps } from '../ui/Field';
import { AllergenIcon } from './AllergenIcon';
import { OTHER_ALLERGEN_ICON } from './allergenIcons';
import { normalizeAllergenName } from './normalize';
import { useAllergenCatalogue } from './useAllergenCatalogue';

/** How many suggestions show under the field at once. A list, not a search page. */
const MAX_SUGGESTIONS = 6;

/**
 * The allergen field of the allergy editor (Q75): any name the catalogue knows finds its entry, and picking one writes
 * the entry's Arabic name. The picture it will show is drawn beside the field as the name is typed.
 *
 * Free text is still accepted. A name the catalogue does not know shows the generic picture, says so under the field,
 * and reaches the platform's list of unknown names; once the platform adds it, this child shows its picture with
 * nothing more to do here (Q77).
 */
export function AllergenInput({ value, onChange, className, ...rest }: Omit<InputProps, 'value' | 'onChange'> & {
  value: string;
  onChange: (value: string) => void;
}) {
  const { t, locale } = useI18n();
  const catalogue = useAllergenCatalogue();
  const [open, setOpen] = useState(false);
  const suggestionsId = useId();

  const entries = catalogue.data ?? [];

  // A retired entry is never suggested, but its names still match, as they do on every other screen.
  const folded = normalizeAllergenName(value);
  const match = folded ? entries.find((entry) => entry.names.includes(folded))?.option : undefined;
  const suggestions = folded && !match
    ? entries
      .filter((entry) => !entry.option.retired && entry.names.some((name) => name.includes(folded)))
      .slice(0, MAX_SUGGESTIONS)
    : [];

  const pick = (option: AllergenOptionResponse) => {
    onChange(option.nameAr);
    setOpen(false);
  };

  return (
    <div
      className="dg-allergen-input"
      onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false); }}
    >
      <div className="dg-allergen-input__field">
        <AllergenIcon icon={match?.icon ?? OTHER_ALLERGEN_ICON} allergen={value.trim() || t('allergy.picker.empty')} size="sm" />
        <Input
          {...rest}
          className={className}
          value={value}
          autoComplete="off"
          aria-controls={suggestions.length > 0 ? suggestionsId : undefined}
          onChange={(event) => { onChange(event.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={(event) => { if (event.key === 'Escape') setOpen(false); }}
        />
      </div>
      {open && suggestions.length > 0 && (
        <div className="dg-allergen-input__suggestions" id={suggestionsId} role="group" aria-label={t('allergy.picker.suggestions')}>
          {suggestions.map(({ option }) => (
            <button
              key={option.key} type="button" className="dg-allergen-input__suggestion"
              // Safari does not focus a button it is clicking, so the field's blur would close the list before the
              // click lands: keeping the focus in the field keeps the list open until the pick.
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => pick(option)}
            >
              <AllergenIcon icon={option.icon} allergen={option.nameAr} size="sm" />
              <span>{locale === 'en' ? option.nameEn : option.nameAr}</span>
              <span className="dg-muted dg-text-sm">{locale === 'en' ? option.nameAr : option.nameEn}</span>
            </button>
          ))}
        </div>
      )}
      {folded && !match && catalogue.isSuccess && (
        <p className="dg-field__hint">{t('allergy.picker.unknown')}</p>
      )}
    </div>
  );
}
